import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import {
  Group,
  Text,
  Checkbox,
  Box,
  ActionIcon,
  Menu,
} from '@mantine/core';
import { MoreHorizontal } from 'react-feather';
import type { Participant } from '../../../types';
import type { GroupMember } from '../../../services/api/groups';
import { ParticipantAvatar } from '../../../components/common';
import classes from './DraggableParticipantCard.module.css';

// Unified participant type for both Participant and GroupMember
export interface DraggableParticipantData {
  id: string; // participant id or user_id
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  group_id?: string | null;
  group_name?: string | null;
}

interface DraggableParticipantCardProps {
  participant: DraggableParticipantData;
  selected?: boolean;
  onSelect?: (participant: DraggableParticipantData, selected: boolean) => void;
  selectionMode?: boolean;
  onAssignToGroup?: (participant: DraggableParticipantData) => void;
  onRemoveFromGroup?: (participant: DraggableParticipantData) => void;
  colorScheme?: string;
  compact?: boolean;
  isDragOverlay?: boolean;
}

// Helper to convert Participant to DraggableParticipantData
// eslint-disable-next-line react-refresh/only-export-components
export function participantToDraggable(p: Participant): DraggableParticipantData {
  return {
    id: p.id,
    user_id: p.user_id,
    first_name: p.first_name,
    last_name: p.last_name,
    email: p.email,
    group_id: p.group_id,
    group_name: p.group_name,
  };
}

// Helper to convert GroupMember to DraggableParticipantData
// eslint-disable-next-line react-refresh/only-export-components
export function groupMemberToDraggable(m: GroupMember, groupId: string): DraggableParticipantData {
  return {
    id: m.user_id,
    user_id: m.user_id,
    first_name: m.first_name,
    last_name: m.last_name,
    email: m.email,
    group_id: groupId,
    group_name: null,
  };
}

// Get initials from name

export function DraggableParticipantCard({
  participant,
  selected = false,
  onSelect,
  selectionMode = false,
  onAssignToGroup,
  onRemoveFromGroup,
  colorScheme: _colorScheme = 'gray',
  compact = false,
  isDragOverlay = false,
}: DraggableParticipantCardProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: participant.user_id,
    data: { participant },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging && !isDragOverlay ? 0.5 : 1,
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    onSelect?.(participant, e.target.checked);
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      className={`${classes.card} ${selected ? classes.selected : ''} ${isDragging ? classes.dragging : ''} ${isDragOverlay ? classes.overlay : ''}`}
      {...attributes}
      {...listeners}
    >
      <Group justify="space-between" gap="sm" wrap="nowrap">
        <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          {selectionMode && (
            <Checkbox
              checked={selected}
              onChange={handleCheckboxChange}
              onClick={(e) => e.stopPropagation()}
              size="sm"
            />
          )}
          {/* MED-1367: same name-based colour as the participants table. */}
          <ParticipantAvatar
            firstName={participant.first_name}
            lastName={participant.last_name}
            size={compact ? 32 : 40}
          />
          <Box style={{ minWidth: 0 }}>
            <Text size="sm" fw={500} truncate>
              {participant.first_name} {participant.last_name}
            </Text>
            {!compact && (
              <Text size="xs" c="dimmed" truncate>
                {participant.email}
              </Text>
            )}
          </Box>
        </Group>

        {(onAssignToGroup || onRemoveFromGroup) && !selectionMode && (
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal size={14} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {onAssignToGroup && (
                <Menu.Item onClick={() => onAssignToGroup(participant)}>
                  Assign to Group
                </Menu.Item>
              )}
              {onRemoveFromGroup && participant.group_id && (
                <Menu.Item color="red" onClick={() => onRemoveFromGroup(participant)}>
                  Remove from Group
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>
    </Box>
  );
}

export default DraggableParticipantCard;
